import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
    Box,
    Button,
    FormHelperText,
    Grid,
    TextField,
    useMediaQuery,
    Alert,
    Snackbar,
    IconButton,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Stack,
    Chip,
    Tooltip
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import AnimateButton from 'ui-component/extended/AnimateButton';
import MainCard from 'ui-component/cards/MainCard';
import Loading from 'components/Loading/Loading';
import { createCalendar, updateCalendar, getCalendarById } from 'services/calendar';
import {
    holidayYear,
    shiftHolidayDateToYear,
    replicateHolidaysForYear,
    collectYearOptions,
    sortHolidaysByDate
} from 'services/calendarUtils';

const newHolidayId = () => `temp_${crypto.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;

/** Alinhado à validação da API (`calendarioFeriado.*.nome` max:255). */
const HOLIDAY_NAME_MAX_LENGTH = 255;

const CalendarPage = ({ ...others }) => {
    const { id: routeCalendarId, action: routeAction } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));

    const calendarId = routeCalendarId;
    const isNew = !calendarId;
    const isView = !isNew && routeAction === 'view';

    const [loading, setLoading] = useState(!isNew);
    const [initialForm, setInitialForm] = useState({ title: '' });
    const [holidays, setHolidays] = useState([]);
    const [workingYear, setWorkingYear] = useState(() => new Date().getFullYear());

    const [draftNome, setDraftNome] = useState('');
    const [draftDate, setDraftDate] = useState(() => {
        const y = new Date().getFullYear();
        return `${y}-01-01`;
    });
    const [editingHolidayId, setEditingHolidayId] = useState(null);

    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });
    const [errorValidate, setErrorValidate] = useState({});

    const showMessage = useCallback((message, severity = 'success') => {
        setFeedback({ open: true, message, severity });
    }, []);

    useEffect(() => {
        setEditingHolidayId(null);
        setDraftNome('');
        setDraftDate(`${workingYear}-01-01`);
    }, [workingYear]);

    useEffect(() => {
        if (isNew) {
            setLoading(false);
            setInitialForm({ title: '' });
            setHolidays([]);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setErrorValidate({});
        getCalendarById(calendarId)
            .then((resp) => {
                if (cancelled) return;
                const data = resp.data;
                setInitialForm({ title: data.nome || '' });
                const list = (data.calendarioFeriado || []).map((d) => ({
                    id: d.id,
                    nome: d.nome,
                    data_feriado: String(d.data_feriado || '').slice(0, 10)
                }));
                setHolidays(list);
            })
            .catch(() => {
                if (!cancelled) showMessage('Não foi possível carregar o calendário.', 'error');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [calendarId, isNew, showMessage]);

    const yearOptions = useMemo(() => {
        const cur = new Date().getFullYear();
        const span = [];
        for (let y = cur - 5; y <= cur + 15; y += 1) span.push(y);
        const fromData = collectYearOptions(holidays, workingYear);
        return [...new Set([...span, ...fromData])].sort((a, b) => a - b);
    }, [holidays, workingYear]);

    const holidaysForYear = useMemo(() => {
        const list = holidays.filter((h) => holidayYear(h.data_feriado) === workingYear);
        return sortHolidaysByDate(list);
    }, [holidays, workingYear]);

    const previousYear = workingYear - 1;
    const previousYearCount = useMemo(
        () => holidays.filter((h) => holidayYear(h.data_feriado) === previousYear).length,
        [holidays, previousYear]
    );

    const removeHoliday = (id) => {
        setHolidays((prev) => prev.filter((h) => String(h.id) !== String(id)));
        if (String(editingHolidayId) === String(id)) {
            setEditingHolidayId(null);
            setDraftNome('');
            setDraftDate(`${workingYear}-01-01`);
        }
    };

    const startEditHoliday = (row) => {
        setEditingHolidayId(row.id);
        setDraftNome(row.nome || '');
        setDraftDate(row.data_feriado || `${workingYear}-01-01`);
    };

    const cancelHolidayForm = () => {
        setEditingHolidayId(null);
        setDraftNome('');
        setDraftDate(`${workingYear}-01-01`);
    };

    const handleAddOrUpdateHoliday = () => {
        const nome = draftNome.trim();
        if (!nome) {
            showMessage('Informe o nome do feriado.', 'warning');
            return;
        }
        if (nome.length > HOLIDAY_NAME_MAX_LENGTH) {
            showMessage(`O nome do feriado pode ter no máximo ${HOLIDAY_NAME_MAX_LENGTH} caracteres.`, 'warning');
            return;
        }
        if (!draftDate) {
            showMessage('Informe a data do feriado.', 'warning');
            return;
        }
        let dataFeriado = draftDate;
        const y = holidayYear(draftDate);
        if (y !== workingYear) {
            dataFeriado = shiftHolidayDateToYear(draftDate, workingYear);
            if (!dataFeriado) {
                showMessage('Data inválida.', 'error');
                return;
            }
        }
        const dup = holidays.some(
            (h) =>
                String(h.id) !== String(editingHolidayId) &&
                h.data_feriado === dataFeriado &&
                h.nome === nome
        );
        if (dup) {
            showMessage('Já existe um feriado com essa data e nome.', 'warning');
            return;
        }

        if (editingHolidayId != null) {
            setHolidays((prev) =>
                prev.map((h) =>
                    String(h.id) === String(editingHolidayId) ? { ...h, nome, data_feriado: dataFeriado } : h
                )
            );
        } else {
            setHolidays((prev) => [...prev, { id: newHolidayId(), nome, data_feriado: dataFeriado }]);
        }
        cancelHolidayForm();
    };

    const handleReplicateFromPreviousYear = () => {
        const additions = replicateHolidaysForYear(holidays, previousYear, workingYear);
        if (additions.length === 0) {
            showMessage(
                previousYearCount === 0
                    ? `Não há feriados cadastrados em ${previousYear} para replicar.`
                    : 'Nenhum feriado novo: datas de ' +
                          previousYear +
                          ' já existem em ' +
                          workingYear +
                          ' ou não puderam ser ajustadas.',
                'info'
            );
            return;
        }
        setHolidays((prev) => [...prev, ...additions]);
        showMessage(`${additions.length} feriado(s) replicado(s) de ${previousYear} para ${workingYear}.`, 'success');
    };

    const buildPayload = (title) => ({
        nome: title.trim(),
        calendarioFeriado: holidays.map(({ nome, data_feriado }) => ({ nome, data_feriado }))
    });

    const handleSave = async (values, { setSubmitting, setErrors }) => {
        setErrorValidate({});
        try {
            const body = buildPayload(values.title);
            if (isNew) {
                await createCalendar(body);
                showMessage('Calendário cadastrado com sucesso.', 'success');
                setTimeout(() => navigate('/calendarios'), 1200);
            } else {
                await updateCalendar(calendarId, body);
                showMessage('Calendário atualizado com sucesso.', 'success');
                setTimeout(() => navigate('/calendarios'), 1200);
            }
        } catch (e) {
            const res = e.response?.data;
            if (res?.errorValidate) {
                setErrorValidate(res.errorValidate);
            }
            const msg = res?.error || res?.message || 'Não foi possível salvar.';
            if (typeof msg === 'string') {
                showMessage(msg, 'error');
            } else {
                showMessage('Não foi possível salvar.', 'error');
            }
            setErrors({ submit: typeof msg === 'string' ? msg : 'Erro' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Snackbar
                open={feedback.open}
                autoHideDuration={6000}
                onClose={() => setFeedback((f) => ({ ...f, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={feedback.severity} onClose={() => setFeedback((f) => ({ ...f, open: false }))}>
                    {feedback.message}
                </Alert>
            </Snackbar>

            {loading && (
                <Grid container alignItems="center" justifyContent="center">
                    <MainCard sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                        <Loading color="#015641" type="cubes" />
                    </MainCard>
                </Grid>
            )}

            {!loading && (
                <MainCard sx={{ p: 2, m: 2 }}>
                    <Typography variant="h4" sx={{ color: 'primary.dark', fontWeight: 600, mb: 1 }}>
                        {isNew ? 'Novo calendário' : 'Calendário'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Defina o nome e os feriados. organize por ano, replique o ano anterior quando fizer sentido e salve uma
                        vez — a API substitui a lista inteira de feriados ao editar.
                    </Typography>

                    <Formik
                        initialValues={initialForm}
                        enableReinitialize
                        validationSchema={Yup.object().shape({
                            title: Yup.string().max(80).trim().required('Título obrigatório')
                        })}
                        onSubmit={handleSave}
                    >
                        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                            <form noValidate onSubmit={handleSubmit} {...others}>
                                <Grid container spacing={matchDownSM ? 1 : 2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            error={Boolean(touched.title && errors.title) || Boolean(errorValidate.nome)}
                                            label="Nome do calendário"
                                            value={values.title}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            name="title"
                                            disabled={isView}
                                            helperText={
                                                (touched.title && errors.title) ||
                                                (errorValidate.nome && String(errorValidate.nome)) ||
                                                ''
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        justifyContent="space-between"
                                        sx={{ mb: 2 }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Feriados por ano
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                                <InputLabel id="yr-label">Ano</InputLabel>
                                                <Select
                                                    labelId="yr-label"
                                                    label="Ano"
                                                    value={workingYear}
                                                    onChange={(e) => setWorkingYear(Number(e.target.value))}
                                                    disabled={isView}
                                                >
                                                    {yearOptions.map((y) => (
                                                        <MenuItem key={y} value={y}>
                                                            {y}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Chip size="small" label={`${holidaysForYear.length} no ano ${workingYear}`} />
                                        </Stack>
                                    </Stack>

                                    {!isView && (
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={handleReplicateFromPreviousYear}
                                                disabled={previousYearCount === 0}
                                            >
                                                Replicar feriados de {previousYear} → {workingYear}
                                            </Button>
                                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                                Copia nomes e datas ajustando o ano (29/02 vira 28/02 se necessário). Não duplica
                                                datas que já existem.
                                            </Typography>
                                        </Stack>
                                    )}

                                    <TableContainer>
                                        <Table
                                            size="small"
                                            sx={{ tableLayout: 'fixed' }}
                                            aria-label="Feriados do ano selecionado"
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: '50%', fontWeight: 600 }}>Feriado</TableCell>
                                                    <TableCell sx={{ width: '30%', fontWeight: 600 }}>Data</TableCell>
                                                    {!isView && (
                                                        <TableCell sx={{ width: '20%', fontWeight: 600 }} align="right">
                                                            Ações
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {holidaysForYear.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={isView ? 2 : 3}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Nenhum feriado em {workingYear}. Adicione abaixo ou replique o
                                                                ano anterior.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {holidaysForYear.map((row) => (
                                                    <TableRow
                                                        key={row.id}
                                                        selected={
                                                            editingHolidayId != null &&
                                                            String(editingHolidayId) === String(row.id)
                                                        }
                                                        hover
                                                    >
                                                        <TableCell
                                                            sx={{
                                                                verticalAlign: 'middle',
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {row.nome}
                                                        </TableCell>
                                                        <TableCell sx={{ verticalAlign: 'middle' }}>
                                                            {row.data_feriado
                                                                ? new Date(row.data_feriado + 'T12:00:00').toLocaleDateString(
                                                                      'pt-BR'
                                                                  )
                                                                : '—'}
                                                        </TableCell>
                                                        {!isView && (
                                                            <TableCell align="right" sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                                <Tooltip title="Editar">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => startEditHoliday(row)}
                                                                    >
                                                                        <EditOutlinedIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Remover">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => removeHoliday(row.id)}
                                                                    >
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {!isView && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                {editingHolidayId != null
                                                    ? 'Editando feriado — ajuste os campos e confirme'
                                                    : 'Incluir novo feriado'}
                                            </Typography>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={5}>
                                                    <TextField
                                                        fullWidth
                                                        label="Nome do feriado"
                                                        value={draftNome}
                                                        onChange={(e) => setDraftNome(e.target.value)}
                                                        size="small"
                                                        inputProps={{ maxLength: HOLIDAY_NAME_MAX_LENGTH }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Data"
                                                        type="date"
                                                        value={draftDate}
                                                        onChange={(e) => setDraftDate(e.target.value)}
                                                        size="small"
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                        <Button
                                                            variant="contained"
                                                            startIcon={
                                                                editingHolidayId != null ? (
                                                                    <EditOutlinedIcon />
                                                                ) : (
                                                                    <AddIcon />
                                                                )
                                                            }
                                                            onClick={handleAddOrUpdateHoliday}
                                                            size="medium"
                                                            sx={{ minHeight: 40 }}
                                                        >
                                                            {editingHolidayId != null ? 'Atualizar' : 'Incluir'}
                                                        </Button>
                                                        {editingHolidayId != null && (
                                                            <Button variant="outlined" size="medium" onClick={cancelHolidayForm} sx={{ minHeight: 40 }}>
                                                                Cancelar
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Nome do feriado: até {HOLIDAY_NAME_MAX_LENGTH} caracteres ({draftNome.length}/
                                                        {HOLIDAY_NAME_MAX_LENGTH}). Ano alvo: {workingYear}. Se a data for de outro
                                                        ano, ajustamos para {workingYear} ao incluir ou atualizar.
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Paper>

                                <Grid container alignItems="center" justifyContent="flex-end" sx={{ mt: 3 }} spacing={1}>
                                    <Grid item>
                                        <AnimateButton>
                                            <Button size="large" variant="outlined" onClick={() => navigate('/calendarios')}>
                                                Voltar
                                            </Button>
                                        </AnimateButton>
                                    </Grid>
                                    {isView && calendarId && (
                                        <Grid item>
                                            <AnimateButton>
                                                <Button
                                                    size="large"
                                                    variant="contained"
                                                    component={Link}
                                                    to={`/calendario/${calendarId}/edit`}
                                                >
                                                    Editar
                                                </Button>
                                            </AnimateButton>
                                        </Grid>
                                    )}
                                    {!isView && (
                                        <Grid item>
                                            <AnimateButton>
                                                <Button size="large" type="submit" variant="contained" disabled={isSubmitting}>
                                                    Salvar
                                                </Button>
                                            </AnimateButton>
                                        </Grid>
                                    )}
                                </Grid>
                                {errors.submit && (
                                    <Box sx={{ mt: 2 }}>
                                        <FormHelperText error>{errors.submit}</FormHelperText>
                                    </Box>
                                )}
                            </form>
                        )}
                    </Formik>
                </MainCard>
            )}
        </>
    );
};

export default CalendarPage;
